import type { ChatOpenOptions, ChatPlugin } from '#types/plugin/chat'
import livechatConfig from '#brand/config/integrations/livechat'
import agentforceChat from '#core/integrations/agentforce-chat'
import clarityAiChat from '#core/integrations/clarity-ai'
import enhancedLiveChat from '#core/integrations/enhanced-live-chat'
import sierraChat from '#core/integrations/sierra-chat'

/**
 * Chat resolver.
 * - `enableSfccAgentforceChat` → Agentforce.
 * - NORA/EMEA → Sierra Chat for locales listed in `configLivechat.clarityRegions`,
 *   otherwise Salesforce MIAW (enhanced-live-chat). Falls back to a disabled
 *   stub if `configLivechat.enabled` is false.
 */
export default defineNuxtPlugin<{ chat: ChatPlugin }>(() => {
  const nuxtApp = useNuxtApp()
  const {
    enableChat,
    configLivechat,
    tempAskMeAnythingSearchHub,
    enableSfccAgentforceChat,
  } = useFeatureFlags()
  const locale = useLocale()

  if (enableSfccAgentforceChat) {
    const provider = agentforceChat()
    const isOpen = useState('agentforceOpen', () => false)
    return {
      provide: {
        chat: {
          ...provider,
          open: (input?: string | ChatOpenOptions) => {
            if (!isOpen.value && input && typeof input === 'object')
              nuxtApp.$gtm.push('agentforce.onInitialize', input.eventLabel, input.buttonLocation)
            provider.open()
          }
        }
      }
    }
  }

  if (!enableChat) {
    return {
      provide: {
        chat: {
          open: () => log.warn('Chat is disabled by feature flag'),
          available: ref(false)
        }
      }
    }
  }

  let chat: ChatPlugin = {
    open: () => log.warn('Chat is not implemented.'),
    available: ref(false)
  }

  if (configLivechat.enabled && configLivechat.clarityRegions?.includes(locale) && tempAskMeAnythingSearchHub) {
    const clarity = clarityAiChat()
    const sierra = sierraChat()

    chat = {
      open: (query, context) =>
        window.CLARITY_HELPER
          ? clarity.open(query, context)
          : sierra.open(query, context),
      available: computed(() => !!(clarity.available?.value || sierra.available?.value)),
      amaAvailable: clarity.available
    }
  }
  else if (configLivechat.enabled && configLivechat.sierraRegions?.includes(locale) && !tempAskMeAnythingSearchHub) {
    chat = sierraChat()
  }
  else if (configLivechat.enabled) {
    chat = enhancedLiveChat(livechatConfig)
  }

  return {
    provide: {
      chat
    }
  }
})
